import { Link } from "react-router-dom";
import { Award, ShieldCheck, BadgeCheck } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "@/components/Seo";
import { SectionHeading, CtaBand, CallButton, GoogleReviewsBlock } from "@/components/Blocks";
import { BUSINESS, BRANDS, HOME_FAQS, PAYMENT_METHODS } from "@/data/site";
import { SERVICES } from "@/data/services";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function AboutPage() {
    return (
        <div>
            <Seo
                title="About Appliance Mania Service & Repair LLC | Brevard & Seminole County FL"
                description="Appliance Mania Service & Repair LLC — insured, EPA certified, 4 years of appliance repair experience. In-home repair across Brevard & Seminole Counties, FL. Call (321) 475-2230."
                path="/about"
                schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
            />
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-3">About us</p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                    Your local appliance repair company — not a call center
                </h1>
                <div className="mt-7 space-y-5 text-slate-600 leading-relaxed">
                    <p>
                        Appliance Mania Service &amp; Repair LLC is a local, professional in-home appliance repair company
                        serving Brevard County and Seminole County, Florida — and serving customers since 2024. We bring
                        4 years of appliance repair experience to every call, and we're insured and EPA certified.
                    </p>
                    <p>
                        We repair refrigerators, freezers, ice makers, washers, dryers, dishwashers, ovens, ranges, and stoves —
                        gas and electric — in your home, on a schedule that works for you.
                    </p>
                    <p>
                        We keep our promises simple and specific: a <strong>$155 service call that is waived with a completed
                        repair</strong>, a clear quote before any work begins, a <strong>6-month warranty on completed
                        repairs</strong>, and honest advice when a repair doesn't make financial sense. Most appointments can be
                        scheduled within about 24 hours, subject to availability. After-hours emergency service may be available
                        from 10 PM to 6 AM — call for availability.
                    </p>
                    <p>
                        We also work with property managers, landlords, and rental-property owners across both counties with
                        $115 service calls, discounted services and repairs, and priority service.
                    </p>
                </div>
                <div className="mt-9 grid sm:grid-cols-3 gap-4">
                    {[
                        [Award, "4 years of appliance repair experience"],
                        [ShieldCheck, "Insured & EPA certified"],
                        [BadgeCheck, "6-month warranty on completed repairs"],
                    ].map(([Icon, text]) => (
                        <div key={text} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm text-center">
                            <Icon className="h-6 w-6 text-red-600 mx-auto" />
                            <p className="mt-2 text-sm font-semibold text-slate-800">{text}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                    <CallButton location="about-page" testid="about-call-btn" />
                </div>
            </section>
            <CtaBand title={<>Appliance trouble? Call <span className="text-amber-400">Appliance Mania.</span></>} sub={`In-home appliance repair across Brevard & Seminole Counties — ${BUSINESS.phone}.`} testid="about-cta" />
        </div>
    );
}

export function BrandsPage() {
    return (
        <div>
            <Seo
                title="Appliance Brands We Service | Whirlpool, GE, LG, Samsung & More | Appliance Mania"
                description="We repair Whirlpool, Maytag, KitchenAid, GE, Frigidaire, Electrolux, LG, Samsung, Bosch, Amana, Hotpoint & other major brands in Brevard & Seminole County, FL. Call (321) 475-2230."
                path="/brands-we-service"
                schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Brands We Service", path: "/brands-we-service" }])]}
            />
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-3">Brands</p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                    Brands we service
                </h1>
                <p className="mt-4 text-slate-600 leading-relaxed">
                    We repair the major residential appliance brands commonly found at Lowe's and other major U.S. appliance
                    retailers — gas and electric models. If your brand isn't listed, call us — if it's a major residential brand,
                    we can very likely help.
                </p>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="brands-grid">
                    {BRANDS.map((b) => (
                        <div key={b} className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm text-center font-heading font-bold text-slate-800">{b}</div>
                    ))}
                    <div className="rounded-xl border border-dashed border-slate-300 px-5 py-4 text-center text-sm font-medium text-slate-500">…and other major residential brands</div>
                </div>
                <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                    Appliance Mania Service &amp; Repair LLC is an independent appliance repair company. We are not affiliated
                    with, endorsed by, or an authorized service provider for any of the manufacturers listed above; brand names
                    are the property of their respective owners and are used here only to describe the appliances we service.
                </p>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 mt-10 mb-4">What we repair, by appliance</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                    {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/${s.slug}`} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-red-400 transition-all">
                            <s.icon className="h-5 w-5 text-red-600 shrink-0" />
                            <span className="font-heading font-bold text-slate-900 text-sm group-hover:text-red-600">{s.name}</span>
                        </Link>
                    ))}
                </div>
            </section>
            <CtaBand title={<>Whatever the brand, call <span className="text-amber-400">Appliance Mania.</span></>} sub={`Tell us the brand and the problem — ${BUSINESS.phone}.`} testid="brands-cta" />
        </div>
    );
}

const EXTRA_FAQS = [
    { q: "Is my service request a confirmed appointment?", a: "No. Submitting the online form sends us a service request. Appliance Mania will contact you to confirm availability and appointment details — nothing is booked until we've confirmed it with you." },
    { q: "Do you service my city?", a: "We serve all of Brevard County and Seminole County, Florida, including Melbourne, Palm Bay, West Melbourne, Rockledge, Cocoa, Cocoa Beach, Titusville, Satellite Beach, Sanford, Lake Mary, Altamonte Springs, Longwood, Oviedo, Winter Springs, and Casselberry." },
    { q: "Do you work with property managers?", a: "Yes — property managers, landlords, and rental-property owners get $115 service calls, discounted services and repairs, and priority service. See our Property Managers page or call (321) 475-2230." },
    { q: "Can I send a photo of the problem?", a: "Yes — our request form lets you attach a photo of the model/serial tag and a photo of the problem or error code. Photos can help us arrive prepared." },
];

export function FaqPage() {
    const all = [...HOME_FAQS, ...EXTRA_FAQS];
    return (
        <div>
            <Seo
                title="Appliance Repair FAQ | Pricing, Warranty, Service Areas | Appliance Mania"
                description="Answers about our $155 service call (waived with completed repair), 6-month warranty, after-hours emergency service, payment methods, and Brevard & Seminole County service areas."
                path="/faq"
                schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]), faqSchema(all)]}
            />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-3">FAQ</p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                    Frequently asked questions
                </h1>
                <div className="mt-7">
                    <Accordion type="single" collapsible>
                        {all.map((f, i) => (
                            <AccordionItem key={i} value={`faqpage-${i}`}>
                                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900" data-testid={`faqpage-trigger-${i}`}>{f.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
                <div className="mt-9 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="payment-methods-box">
                    <h2 className="font-heading text-lg font-bold text-slate-900">Payment methods we accept</h2>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {PAYMENT_METHODS.map((p) => (
                            <span key={p} className="rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700">{p}</span>
                        ))}
                    </div>
                </div>
            </section>
            <CtaBand title={<>Still have a question? Call <span className="text-amber-400">Appliance Mania.</span></>} sub={`We're happy to talk it through — ${BUSINESS.phone}.`} testid="faq-cta" />
        </div>
    );
}

export function ReviewsPage() {
    return (
        <div>
            <Seo
                title="Customer Reviews | Appliance Mania Service & Repair | Brevard & Seminole FL"
                description="Read genuine Google reviews for Appliance Mania Service & Repair LLC — in-home appliance repair across Brevard & Seminole Counties, FL. Call (321) 475-2230."
                path="/reviews"
                schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }])]}
            />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] text-center mb-8">
                    Customer reviews
                </h1>
                <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm" data-testid="reviews-google-section">
                    <GoogleReviewsBlock />
                </div>
                <p className="mt-6 text-center text-sm text-slate-500 max-w-xl mx-auto">
                    Reviews live on our Google Business Profile, where every review is posted by a real customer through Google.
                </p>
                <div className="mt-7 text-center">
                    <CallButton location="reviews-page" testid="reviews-call-btn" />
                </div>
            </section>
        </div>
    );
}

export function NotFoundPage() {
    return (
        <div>
            <Seo title="Page Not Found | Appliance Mania Service & Repair" description="The page you requested could not be found." path="/404" />
            <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="not-found-page">
                <p className="font-heading text-7xl font-extrabold text-slate-200">404</p>
                <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900">This page isn't on our route</h1>
                <p className="mt-3 text-slate-600">The page you're looking for doesn't exist — but if your appliance is broken, we're easy to find.</p>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                    <CallButton location="404-page" testid="404-call-btn" />
                    <Link to="/" className="inline-flex items-center justify-center gap-2 border border-slate-300 bg-white px-6 py-3.5 rounded-xl font-bold text-slate-800 hover:border-red-500 hover:text-red-600 transition-all" data-testid="404-home-btn">
                        Back to homepage
                    </Link>
                </div>
            </section>
        </div>
    );
}
